import { jwtDecode } from 'jwt-decode';

// Common role constants for backward compatibility
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  OFFICIAL_1: 'official_1',
  OFFICIAL_2: 'official_2',
  OFFICIAL_3: 'official_3'
};

/**
 * Get all role names from roles data
 * @param {Array} roles - Array of role objects from API
 * @returns {Array} Array of role names
 */
export const getRoleNames = (roles) => {
  console.log({ roles })
  return roles?.map(role => role.name) || [];
};

/**
 * Create role hierarchy map from roles data
 * @param {Array} roles - Array of role objects from API
 * @returns {Object} Role hierarchy object
 */
export const createRoleHierarchy = (roles) => {
  const hierarchy = {};
  roles?.forEach(role => {
    hierarchy[role.name] = role.id || 0;
  });
  return hierarchy;
};

/**
 * Get the current user's role from the JWT token stored in localStorage
 * @returns {string|null} The user's role or null if no valid token exists
 */
export const getCurrentUserRole = () => {
  try {
    const token = localStorage.getItem('giz_superaccess');
    if (!token) return null;

    const decodedToken = jwtDecode(token);
    return decodedToken.role || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if the current user has a specific role
 * @param {string} requiredRole - The role to check for
 * @returns {boolean} True if user has the required role, false otherwise
 */
export const hasRole = (requiredRole) => {
  const currentRole = getCurrentUserRole();
  return currentRole === requiredRole;
};

/**
 * Check if the current user has any of the specified roles
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean} True if user has any of the specified roles, false otherwise
 */
export const hasAnyRole = (roles) => {
  const currentRole = getCurrentUserRole();
  console.log({ currentRole, roles })
  return roles.includes(currentRole);
};

/**
 * Check if the current user is a super admin
 * @returns {boolean} True if user is super_admin, false otherwise
 */
export const isSuperAdmin = () => {
  return hasRole('super_admin');
};

/**
 * Check if the current user is an admin (admin or super_admin)
 * @returns {boolean} True if user is admin or super_admin, false otherwise
 */
export const isAdmin = () => {
  return hasAnyRole(['super_admin', 'admin']);
};

/**
 * Check if the current user is a manager
 * @returns {boolean} True if user is manager, false otherwise
 */
export const isManager = () => {
  return hasRole('manager');
};

/**
 * Check if the current user is any official level
 * @returns {boolean} True if user is official_1, official_2, or official_3, false otherwise
 */
export const isOfficial = () => {
  return hasAnyRole(['official_1', 'official_2', 'official_3']);
};

/**
 * Check if the current user has administrative privileges (super_admin, admin, or manager)
 * @returns {boolean} True if user has admin privileges, false otherwise
 */
export const hasAdminPrivileges = () => {
  return hasAnyRole(['super_admin', 'admin', 'manager']);
};

// Default role hierarchy for backward compatibility
const DEFAULT_ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 6,
  [ROLES.ADMIN]: 5,
  [ROLES.MANAGER]: 4,
  [ROLES.OFFICIAL_1]: 3,
  [ROLES.OFFICIAL_2]: 2,
  [ROLES.OFFICIAL_3]: 1
};

/**
 * Check if current user has higher or equal privilege than specified role
 * @param {string} compareRole - Role to compare against
 * @param {Array} [rolesData] - Optional array of role objects from API
 * @returns {boolean} True if current user has higher/equal privilege, false otherwise
 */
export const hasMinimumRole = (compareRole, rolesData) => {
  const currentRole = getCurrentUserRole();

  if (!currentRole) return false;

  // Use API data if available, otherwise fall back to default hierarchy
  const hierarchy = rolesData ? createRoleHierarchy(rolesData) : DEFAULT_ROLE_HIERARCHY;

  if (!hierarchy[currentRole] || !hierarchy[compareRole]) {
    return false;
  }
  return hierarchy[currentRole] >= hierarchy[compareRole];
};

/**
 * Check if current user has higher privilege than specified role
 * @param {string} compareRole - Role to compare against
 * @param {Array} [rolesData] - Optional array of role objects from API
 * @returns {boolean} True if current user has higher privilege, false otherwise
 */
export const hasHigherRole = (compareRole, rolesData) => {
  const currentRole = getCurrentUserRole();

  if (!currentRole) return false;

  // Use API data if available, otherwise fall back to default hierarchy
  const hierarchy = rolesData ? createRoleHierarchy(rolesData) : DEFAULT_ROLE_HIERARCHY;

  if (!hierarchy[currentRole] || !hierarchy[compareRole]) {
    return false;
  }
  return hierarchy[currentRole] > hierarchy[compareRole];
};

/**
 * Get role hierarchy level
 * @param {string} role - Role to get level for
 * @param {Array} [rolesData] - Optional array of role objects from API
 * @returns {number} Role level (higher = more privileges)
 */
export const getRoleLevel = (role, rolesData) => {
  // Use API data if available, otherwise fall back to default hierarchy
  const hierarchy = rolesData ? createRoleHierarchy(rolesData) : DEFAULT_ROLE_HIERARCHY;
  return hierarchy[role] || 0;
};

/**
 * Get roles that have lower privilege than the current user's role
 * @param {Array} rolesData - Array of role objects from API
 * @returns {Array} Array of role objects with lower privilege
 */
export const getAvailableRolesForCreation = (rolesData) => {
  console.log("utils", { rolesData })
  const currentRole = getCurrentUserRole();
  const hierarchy = createRoleHierarchy(rolesData);
  console.log({ hierarchy })
  const currentUserLevel = hierarchy[currentRole] || 0;
  console.log({ currentUserLevel })

  return rolesData?.filter(role => {
    const roleLevel = hierarchy[role.name] || 0;
    console.log({ roleLevel })
    return roleLevel > currentUserLevel;
  }) || [];
};

/**
 * Get the full decoded JWT token
 * @returns {object|null} The decoded token object or null if invalid
 */
export const getCurrentUser = () => {
  try {
    const token = localStorage.getItem('giz_superaccess');
    if (!token) return null;

    return jwtDecode(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};
/* Human-readable (Albanian) names for dashboard roles */
export const ROLE_LABELS = {
  super_admin: 'Super administrator',
  admin: 'Administrator',
  manager: 'Menaxher',
  official_1: 'Zyrtar – niveli 1',
  official_2: 'Zyrtar – niveli 2',
  official_3: 'Zyrtar – niveli 3',
  user: 'Qytetar',
};

export const getRoleLabel = (role) =>
  ROLE_LABELS[role] || (role ? role.replace(/_/g, ' ') : 'Përdorues');
